import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@arcadeai/design-system/components/ui/atoms/card";
import { Separator } from "@arcadeai/design-system/components/ui/atoms/separator";
import { Edit, User } from "lucide-react";
import type React from "react";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import { useGetCustomer, useSuspenseGetSubscription } from "../../api/hooks";
import { CustomerEditSheet } from "./customer-edit-sheet";

type AccountDetailsCardProps = {
  formatDate: (date: string) => string;
};

export const AccountDetailsCard: React.FC<AccountDetailsCardProps> = ({
  formatDate,
}) => {
  const { organizationId } = useOrgProjectRouteContext();
  const { data: subscription } = useSuspenseGetSubscription(organizationId);
  useGetCustomer(organizationId);

  const customer = subscription?.customer;

  if (!customer) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <User className="h-4 w-4 text-brand-text" />
          Account Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <h4 className="mb-3 font-semibold text-[11px] text-muted-foreground uppercase tracking-wider">
            Personal Details
          </h4>
          <div className="space-y-2">
            <div className="group flex items-center justify-between">
              <span className="text-muted-foreground text-sm">Name</span>
              <div className="flex items-center gap-3 rounded-md px-2 py-1 transition-colors hover:bg-muted/50">
                <span className="font-medium text-foreground text-sm">
                  {customer.name}
                </span>
                <CustomerEditSheet
                  currentName={customer.name}
                  trigger={
                    <Button
                      className="h-7 w-7 p-0 transition-all duration-200 hover:bg-primary/10 hover:text-brand-text"
                      size="sm"
                      variant="ghost"
                    >
                      <Edit className="size-4" />
                      <span className="sr-only">Edit name</span>
                    </Button>
                  }
                />
              </div>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground text-sm">Email</span>
              <span className="truncate font-medium text-sm">
                {subscription.customer.email}
              </span>
            </div>
          </div>
        </div>
        <Separator />
        <div>
          <h4 className="mb-3 font-semibold text-[11px] text-muted-foreground uppercase tracking-wider">
            Subscription
          </h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground text-sm">Start Date</span>
              <span className="font-medium text-sm">
                {formatDate(subscription.start_date)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground text-sm">
                Auto Collection
              </span>
              <Badge
                className="text-xs"
                variant={customer.auto_collection ? "default" : "outline"}
              >
                {customer.auto_collection ? "Enabled" : "Disabled"}
              </Badge>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
